import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import {
  GridGroupNode,
  GridRenderCellParams,
  GridRowId,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import { ReactComponent as ChevronDownIcon } from '@/assets/icons/ChevronDownRounded.svg';
import { ReactComponent as ChevronRightIcon } from '@/assets/icons/ChevronRightRounded.svg';
import { StatusChip } from '@/shared/ui/StatusChip';

import {
  cellContainerSx,
  getArrowButtonSx,
  getArrowIconSx,
  getCellStackSx,
  groupLabelSx,
  groupRowChipSx,
} from './GeneratikGroupingCell.styles';
import { MAX_TREE_INDENT_DEPTH, TREE_INDENT_STEP_REM } from './GeneratikTable.constants';
import { isUngroupedKey, sumGroupItemPrice } from './GeneratikTable.utils';
import { useOptionalGeneratikTableContext } from './GeneratikTableContext';
import GeneratikTableGroupTotalPill from './GeneratikTableGroupTotalPill';

interface GeneratikGroupingCellProps extends GridRenderCellParams {
  chipBgColor?: string;
  userType?: string;
  arrowPosition?: 'left' | 'right';
  useSvgIcons?: boolean;
  paddingLeft?: string | number;
  showLabel?: boolean;
  ungroupedLabel?: string;
  children?: ReactNode;
}

const DEFAULT_CHIP_BG = '#8E6DE1';

type GroupChildrenApi = {
  getRowGroupChildren?: (params: { groupId: GridRowId }) => GridRowId[];
};

const resolveGroupTotalPrice = (
  api: GridRenderCellParams['api'],
  groupId: GridRowId,
  getGroupItemPrice: (row: GridValidRowModel) => number | undefined,
): number | undefined => {
  const getRowGroupChildren = (api as unknown as GroupChildrenApi).getRowGroupChildren;
  if (!getRowGroupChildren) return undefined;

  const descendants = getRowGroupChildren({ groupId })
    .map((childId) => api.getRow(childId))
    .filter((row): row is GridValidRowModel => !!row);

  return sumGroupItemPrice(descendants, getGroupItemPrice);
};

const GeneratikGroupingCell = (props: GeneratikGroupingCellProps) => {
  const {
    rowNode,
    api,
    id,
    chipBgColor = DEFAULT_CHIP_BG,
    arrowPosition = 'left',
    useSvgIcons = false,
    paddingLeft,
    showLabel,
    ungroupedLabel = '—',
    children,
  } = props;

  const { t } = useTranslation();
  const tableContext = useOptionalGeneratikTableContext();
  const { getGroupItemPrice, currency, groupPriceForPeriod, groupRowCountLabel } =
    tableContext ?? {};
  const theme = useTheme();

  const depth = rowNode?.depth ?? 0;
  const isGroup = rowNode?.type === 'group';
  const groupNode = isGroup ? (rowNode as GridGroupNode) : undefined;
  const hasChildren = (groupNode?.children?.length ?? 0) > 0;
  const isExpanded = groupNode?.childrenExpanded ?? false;
  const isAutoGeneratedGroup = !!(groupNode as { isAutoGenerated?: boolean } | undefined)
    ?.isAutoGenerated;

  const resolvedPaddingLeft =
    paddingLeft ??
    (arrowPosition === 'left'
      ? `${0.75 + Math.min(depth, MAX_TREE_INDENT_DEPTH) * TREE_INDENT_STEP_REM}rem`
      : '3.875rem');

  const showLabelResolved = showLabel ?? isAutoGeneratedGroup;

  const toggleExpansion = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isGroup) return;
    // @ts-ignore — setRowChildrenExpansion is on the premium api
    api.setRowChildrenExpansion(id, !isExpanded);
  };

  const arrow =
    isGroup && hasChildren ? (
      <IconButton
        size="small"
        onClick={toggleExpansion}
        data-testid="grouping-cell-expand-toggle"
        data-expanded={isExpanded}
        sx={getArrowButtonSx(useSvgIcons, chipBgColor)}
      >
        {useSvgIcons ? (
          isExpanded ? (
            <ChevronDownIcon />
          ) : (
            <ChevronRightIcon />
          )
        ) : (
          <KeyboardArrowDownRoundedIcon sx={getArrowIconSx(isExpanded)} />
        )}
      </IconButton>
    ) : null;

  const rawKey = groupNode?.groupingKey;
  const labelText =
    typeof rawKey === 'string' && isUngroupedKey(rawKey) ? ungroupedLabel : (rawKey as string);

  const groupTotalPrice =
    isAutoGeneratedGroup && getGroupItemPrice
      ? resolveGroupTotalPrice(api, id, getGroupItemPrice)
      : undefined;

  const groupRowCountText =
    isAutoGeneratedGroup && groupRowCountLabel
      ? groupRowCountLabel(groupNode?.children?.length ?? 0)
      : undefined;

  return (
    <Box sx={cellContainerSx}>
      <Stack direction="row" alignItems="center" gap={1} sx={getCellStackSx(resolvedPaddingLeft)}>
        {arrowPosition === 'left' && arrow}
        {showLabelResolved && labelText !== undefined && labelText !== null && (
          <Typography sx={groupLabelSx}>{labelText}</Typography>
        )}
        {groupRowCountText !== undefined && (
          <StatusChip
            label={groupRowCountText}
            bg={theme.palette.accent.main}
            sx={groupRowChipSx}
            testId="group-row-count"
          />
        )}
        {children}
        {groupTotalPrice !== undefined && (
          <GeneratikTableGroupTotalPill
            label={t('Total')}
            price={groupTotalPrice}
            currency={currency}
            priceForPeriod={groupPriceForPeriod}
            asChip
          />
        )}
        {arrowPosition === 'right' && arrow}
      </Stack>
    </Box>
  );
};

export default GeneratikGroupingCell;
